import { ComponentProps, ComponentType } from 'react';
import { ViewStyle } from 'react-native';

import ErrorBoundary, { ErrorBoundaryProps } from './ErrorBoundary';

type Options<T = Dict> = {
  screen?: string;
  key?: string;
  fallback?: ErrorBoundaryProps['fallback'];
  renderFallback?: (
    props: T & {
      resetError?: () => void;
      style?: ViewStyle;
    },
  ) => ErrorBoundaryProps['fallback'];
  tags?: Record<string, string>;
};

const withErrorBoundary = <T,>(
  WrappedComponent: ComponentType<T>,
  {
    screen,
    key,
    fallback,
    renderFallback,
    tags = {},
  }: Options<ComponentProps<ComponentType<T>>>,
) => {
  const ComponentWithErrorBoundary = (
    props: ComponentProps<ComponentType<T>>,
  ) => {
    return (
      <ErrorBoundary
        fallback={renderFallback ? renderFallback(props) : fallback}
        beforeCapture={([scope], errorUUID) => {
          scope.setTag('errorUUID', errorUUID);

          if (screen) {
            scope.setTag('screen', screen);
          }

          if (key) {
            scope.setTag('key', key);
          }

          Object.entries(tags).forEach(([key, value]) =>
            scope.setTag(key, value),
          );
        }}
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };

  return ComponentWithErrorBoundary;
};

export default withErrorBoundary;
